import { getCompanyFormFields, resetForm, selectAllFields, selectFields, selectForm, updateValue, updateValues } from '../formSlice'
import { errorMessage, finalExp, isValidNip } from 'utils/validation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isPolishCountry } from '../Contract/fields'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import CompaniesPopup from './CompaniesPopup'
import Select from 'components/inputs/Select'
import Submit from 'components/inputs/Submit'
import Input from 'components/inputs/Input'
import { STATUS } from 'utils/constants'
import { findCompany } from 'rest/urls'
import { GET } from 'rest/request'

const Form = ({ block, submit, cancel, requiredInputs }) => {
  const { companyData = '', ...form } = useSelector(selectForm)
  const formFields = useSelector(selectFields)
  const allFields = useSelector(selectAllFields)
  const fields = useMemo(
    () => getCompanyFormFields(formFields, allFields, requiredInputs?.company),
    [allFields, formFields, requiredInputs?.company]
  )
  const hasHomeNr = fields.some(({ inputName }) => inputName === 'homeNr')
  const [warning, setWarning] = useState({})
  // która akcja (delete / change / add) jest w toku – żeby pokazać loader na właściwym przycisku
  const [pendingAction, setPendingAction] = useState(null)
  const { popup, openPopup } = usePopupState()
  const nip = form.nip || ''

  const lastSearch = useRef('')

  const dispatch = useDispatch()
  const { t } = useTranslation('form')

  const refs = []
  const enter = t('enter')

  useEffect(() => {
    if (!block) setPendingAction(null)
  }, [block])

  useEffect(() => {
    lastSearch.current = nip.length === 10 ? true : ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id])

  useEffect(() => {
    if (nip.length === 10 && companyData) lastSearch.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (lastSearch.current !== false && lastSearch.current !== nip && nip.length === 10) {
      document.activeElement.blur()

      if (lastSearch.current !== true) {
        dispatch(updateValues({ companyStatus: STATUS.pending }))
        openPopup('companies')
      }

      (async () => {
        try {
          const response = await GET(`${findCompany}${nip}`)
          dispatch(updateValues({
            companyData: Array.isArray(response?.companies) ? response.companies : response?.error,
            companyStatus: STATUS.succeed
          }))

        } catch (err) {
          dispatch(updateValues({ companyStatus: STATUS.failed }))
        }
      })()
    }

    if (nip.length === 10) lastSearch.current = nip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, nip])

  const changeHandler = e => {
    if (!e) return
    const { name, value } = e.target
    dispatch(updateValue({ name, value }))
    if (
      name === 'nip'
        ? (warning.nip && value.match(finalExp.nip))
        : (warning[name] && value)
    ) {
      setWarning(prev => ({ ...prev, [name]: false }))
    }
  }

  const blurHandler = e => {
    if (!e) return
    const { name, value } = e.target

    if (
      name === 'nip'
        ? (warning.nip && value.match(finalExp.nip))
        : (warning[name] && value)
    ) {
      setWarning(prev => ({
        ...prev,
        [name]: name === 'nip'
          ? value.length < 10 ? errorMessage(t, 10 - value.length) : (!value.match(finalExp.nip) || !isValidNip(value)) ? t('incorrectTIN') : false
          : false
      }))
    }
  }

  const enterClicked = ref => {
    const index = refs.findIndex(r => Object.is(r, ref))
    if (index === -1) return
    if (refs[index + 1]) refs[index + 1].focus()
    else if (index + 1 === refs.length && !block) validation()
  }

  const postalCodeWarning = () => {
    const value = (form.postalCode || '').trim()
    if (!value || !fields.some(({ inputName }) => inputName === 'postalCode')) return false
    return isPolishCountry(form.country) && !value.match(finalExp.postalCode) ? '**-***' : false
  }

  const validation = (action = form.id ? 'change' : 'add') => {
    const nipOK = !!nip.match(finalExp.nip) && isValidNip(nip)
    const postalCodeError = postalCodeWarning()
    const formIsValid = nipOK && !postalCodeError && !fields.some((f) => f.isRequired && !`${form[f.inputName] ?? ''}`.trim())

    if (formIsValid) {
      const keys = fields.map(({ inputName }) => inputName)
      const body = Object.entries(form).reduce((r, [k, v]) => {
        if (keys.includes(k)) r = { ...r, [k]: v }
        return r
      }, {})
      body.nip = nip
      lastSearch.current = ''
      setPendingAction(action)
      form.id ? submit(body, form.id) : submit(body)

    } else {
      const newErrors = {
        nip: nip.length < 10 ? errorMessage(t, 10 - nip.length) : !nipOK ? t('incorrectTIN') : false
      }
      fields.forEach(f => {
        if (f.isRequired && !`${form[f.inputName] ?? ''}`.trim()) {
          newErrors[f.inputName] = t('thisFieldIsRequired')
        }
      })
      if (postalCodeError && !newErrors.postalCode) newErrors.postalCode = postalCodeError
      setWarning(newErrors)
    }
  }

  const counterIcon = Array.isArray(companyData)
    ? <div
      className='element__icon'
      onClick={() => {
        openPopup('companies')
      }}>
      {companyData.length}
    </div>
    : undefined

  return (
    <>
      {popup === 'companies' && <CompaniesPopup
        changeNip={() => {
          if (refs[0]) refs[0]?.focus()
        }}
        selectData={({ raport = {}, ...data } = {}) => {
          const keys = fields.map(({ inputName }) => inputName)
          const values = Object.entries(data).reduce((r, [k, v]) => {
            if (keys.includes(k)) {
              // numer budynku / lokalu wpisujemy do ulicy tylko, gdy formularz nie ma osobnych pól
              const val = k === 'streetName' && !hasHomeNr ? `${v} ${data?.homeNr}${data.flatNr ? `/${data.flatNr}` : ''}` : v
              r = { ...r, [k]: val }
            }
            return r
          }, {})

          if (raport.addressHeadCountryName) values['country'] = raport.addressHeadCountryName
          if (raport.detailLegalFormId) {
            const companyFormId = fields?.find(({ inputName }) => inputName === 'companyFormId')

            if (companyFormId && companyFormId.options) {
              const detailLegalFormId = Number(raport.detailLegalFormId)
              const option = companyFormId.options?.find(({ code }) => Number(code) === detailLegalFormId)

              if (option) values['companyFormId'] = option.value
            }
          }

          dispatch(updateValues(values))
        }}
      />}

      <div className='billing__form form'>
        <p className='form__title'>
          <span>{t(form.id ? 'editCompany' : 'addCompany')}</span>
        </p>

        <Input
          required
          ref={r => refs.push(r)}
          value={nip}
          name='nip'
          label={t('tin')}
          minLength='10'
          maxLength='10'
          icon={counterIcon}
          iconPosRight
          disabled={block}
          warning={warning.nip}
          placeholder={`${enter} ${t('tin')}`}
          enterClicked={enterClicked}
          changeHandler={changeHandler}
          blurHandler={blurHandler} />

        {nip.length === 10 && fields.length > 0 && fields?.map(({ id, isRequired, inputName, inputdefaultValue, inputType, inputLabel, placeholder, options }) => {
          if (inputdefaultValue && !form[inputName]) dispatch(updateValue({ name: inputName, value: inputdefaultValue }))
          return inputType === 'select' ? (
            <Select
              key={id}
              disabled={block}
              name={inputName}
              label={inputLabel}
              required={isRequired}
              warning={warning[inputName]}
              value={form[inputName] || ''}
              setValue={value => {
                changeHandler({ target: { value, name: inputName } })
              }}
              options={options}
            />
          ) : inputType === 'text' ? (
            <Input
              key={id}
              disabled={block}
              required={isRequired}
              ref={r => refs.push(r)}
              name={inputName}
              warning={warning[inputName]}
              placeholder={placeholder || `${enter} ${inputLabel}`}
              label={inputLabel}
              value={form[inputName] || ''}
              changeHandler={changeHandler}
              enterClicked={enterClicked}
            />
          ) : false
        })}

        {form.id ? <div className='center'>
          <ArrowBtn
            enlarged
            showArrow={false}
            className='--black'
            text={t('delete')}
            disabled={block && pendingAction !== 'delete'}
            isLoading={block && pendingAction === 'delete'}
            action={() => {
              if (block) return
              setPendingAction('delete')
              submit(null, form.id)
            }}
          />

          <ArrowBtn
            enlarged
            showArrow={false}
            text={t('change')}
            disabled={block && pendingAction !== 'change'}
            isLoading={block && pendingAction === 'change'}
            action={() => !block && nip.length === 10 && validation('change')}
          />
        </div> :
          <Submit
            text={t('add')}
            block={block}
            isLoading={block}
            action={() => !block && validation('add')} />}

        {cancel && <button
          className='button --back'
          disabled={block}
          onClick={() => {
            if (block) return
            dispatch(resetForm())
            cancel()
          }}>
          <span>{t('cancel')}</span>
        </button>}
      </div>
    </>
  )
}

export default Form